import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { SERVICES } from "@/data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Emergent hosting talks to the FastAPI backend; Hostinger production builds
// (REACT_APP_BACKEND_URL empty) call the same-origin PHP API instead.
const CONTACT_URL = BACKEND_URL ? `${BACKEND_URL}/api/contact` : "/api/contact.php";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-mist placeholder:text-mist/30 focus:border-electric/60 outline-none transition-colors duration-200";

const Field = ({ label, htmlFor, children, required }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs uppercase tracking-[0.15em] text-mist/50 font-semibold mb-2">
      {label} {required && <span className="text-electric">*</span>}
    </label>
    {children}
  </div>
);

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", website: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(CONTACT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setDone(true);
      toast.success("Enquiry sent — we'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please try calling or WhatsApping us instead.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="bg-navy-light border border-electric/30 rounded-3xl p-10 lg:p-14 text-center grain relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-20" aria-hidden="true" />
        <div className="relative">
          <CheckCircle2 size={52} className="text-electric mx-auto" />
          <h3 className="mt-6 font-display text-3xl font-extrabold text-mist tracking-tight">Message received.</h3>
          <p className="mt-3 text-slatec max-w-md mx-auto leading-relaxed">
            Thank you, {form.name.split(" ")[0]}. Your enquiry has landed with the Click World Online team — expect to
            hear from us shortly.
          </p>
          <button
            onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", company: "", website: "", service: "", message: "" }); }}
            data-testid="contact-send-another"
            className="mt-8 text-electric text-sm font-semibold hover:underline"
          >
            Send another enquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="bg-navy-light border border-white/10 rounded-3xl p-8 lg:p-12 grain relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-15" aria-hidden="true" />
      <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field label="Name" htmlFor="cf-name" required>
          <input id="cf-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
        </Field>
        <Field label="Email" htmlFor="cf-email" required>
          <input id="cf-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
        </Field>
        <Field label="Phone" htmlFor="cf-phone">
          <input id="cf-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 …" className={inputCls} />
        </Field>
        <Field label="Company" htmlFor="cf-company">
          <input id="cf-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Business name" className={inputCls} />
        </Field>
        <Field label="Website" htmlFor="cf-website">
          <input id="cf-website" data-testid="contact-website-input" type="url" value={form.website} onChange={set("website")} placeholder="https://…" className={inputCls} />
        </Field>
        <Field label="Service Interested In" htmlFor="cf-service">
          <select id="cf-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`}>
            <option value="" className="bg-navy">Select a service…</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title} className="bg-navy">{s.title}</option>
            ))}
            <option value="Not sure yet" className="bg-navy">Not sure yet</option>
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Message" htmlFor="cf-message" required>
            <textarea id="cf-message" data-testid="contact-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your business and what you'd like to achieve…" className={`${inputCls} resize-none`} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep disabled:opacity-60 text-white font-semibold px-10 py-4 rounded-full transition-colors duration-300"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {loading ? "Sending…" : "Send Enquiry"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
